/**
 * Pagina do Dashboard de Leads e Funil de Conversao
 * Route: /dashboard/leads
 */

import { useState, useEffect, useCallback } from "react";
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Input, Badge, Select, useToast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  SkeletonCard, SkeletonTable,
} from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

// ============ Types ============

interface LeadStats {
  totalLeads: number;
  completed: number;
  inProgress: number;
  abandoned: number;
  conversionRate: number;
  avgCompletionMinutes: number;
  channels: Record<string, number>;
  funnel: FunnelStep[];
}

interface FunnelStep {
  step: number;
  reached: number;
  label: string;
}

interface Lead {
  id: string;
  nome: string | null;
  cpf: string | null;
  whatsapp: string | null;
  channel: string;
  currentStep: number;
  totalSteps: number;
  status: string;
  otpVerified: boolean;
  startedAt: string;
  lastActivityAt: string;
  completedAt: string | null;
  createdAt: string;
}

type StatusFilter = "todos" | "in_progress" | "completed" | "abandoned";

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  in_progress: { label: "Em Andamento", color: "bg-yellow-100 text-yellow-800" },
  completed: { label: "Completo", color: "bg-green-100 text-green-800" },
  abandoned: { label: "Abandonado", color: "bg-red-100 text-red-800" },
};

const CHANNEL_LABELS: Record<string, string> = {
  app: "App",
  web: "Web",
  whatsapp: "WhatsApp",
  link: "Link",
};

// ============ API ============

async function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || "Erro na requisicao");
  return data;
}

// ============ Component ============

export function LeadsFunnelPage() {
  const { toast } = useToast();
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [leadsLoading, setLeadsLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("todos");
  const [channelFilter, setChannelFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Re-engage modal
  const [reengageLead, setReengageLead] = useState<Lead | null>(null);
  const [reengageMsg, setReengageMsg] = useState("");
  const [reengaging, setReengaging] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const data = await apiFetch(`/v1/admin/leads-dashboard/stats${qs}`);
      setStats(data.data);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, toast]);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "20");
      if (statusFilter !== "todos") params.set("status", statusFilter);
      if (channelFilter) params.set("channel", channelFilter);
      if (dateFrom) params.set("dateFrom", dateFrom);
      if (dateTo) params.set("dateTo", dateTo);
      if (search) params.set("search", search);
      const data = await apiFetch(`/v1/admin/leads-dashboard/list?${params.toString()}`);
      setLeads(data.data || []);
      setTotal(data.meta?.total || 0);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "error" });
    } finally {
      setLeadsLoading(false);
    }
  }, [page, statusFilter, channelFilter, dateFrom, dateTo, search, toast]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const handleReengage = async () => {
    if (!reengageLead) return;
    setReengaging(true);
    try {
      await apiFetch(`/v1/admin/leads-dashboard/${reengageLead.id}/reengage`, {
        method: "POST",
        body: JSON.stringify({ mensagem: reengageMsg || undefined }),
      });
      toast({ variant: "success", title: "Mensagem enviada" });
      setReengageLead(null);
      setReengageMsg("");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "error" });
    } finally {
      setReengaging(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatCpf = (cpf: string | null) =>
    cpf ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "-";

  const totalPages = Math.ceil(total / 20);

  // Funnel bar chart max
  const funnelMax = stats?.funnel?.[0]?.reached || 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Leads e Funil de Conversao</h1>
          <p className="text-sm text-muted-foreground">
            Acompanhe a jornada dos leads no onboarding
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-36"
            placeholder="De"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-36"
            placeholder="Ate"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : stats ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium uppercase text-muted-foreground">Total Leads</p>
                <p className="mt-1 text-2xl font-bold text-foreground">{stats.totalLeads}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium uppercase text-muted-foreground">Taxa Conversao</p>
                <p className="mt-1 text-2xl font-bold text-green-600">{stats.conversionRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium uppercase text-muted-foreground">Tempo Medio</p>
                <p className="mt-1 text-2xl font-bold text-foreground">
                  {stats.avgCompletionMinutes > 60
                    ? `${Math.round(stats.avgCompletionMinutes / 60)}h`
                    : `${stats.avgCompletionMinutes}min`}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium uppercase text-muted-foreground">Abandonos</p>
                <p className="mt-1 text-2xl font-bold text-red-600">{stats.abandoned}</p>
              </CardContent>
            </Card>
          </div>

          {/* Funnel Chart + Channels */}
          <div className="grid gap-4 md:grid-cols-3">
            {/* Funnel */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Funil de Conversao</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.funnel.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">
                    Sem dados de funil disponiveis
                  </p>
                ) : (
                  <div className="space-y-3">
                    {stats.funnel.map((step, i) => {
                      const pct = funnelMax > 0 ? Math.round((step.reached / funnelMax) * 100) : 0;
                      const dropOff =
                        i > 0
                          ? stats.funnel[i - 1].reached - step.reached
                          : 0;
                      return (
                        <div key={step.step}>
                          <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="font-medium text-foreground">{step.label}</span>
                            <span className="text-muted-foreground">
                              {step.reached} leads
                              {dropOff > 0 && (
                                <span className="ml-2 text-red-500">(-{dropOff})</span>
                              )}
                            </span>
                          </div>
                          <div className="h-6 w-full overflow-hidden rounded-md bg-muted">
                            <div
                              className="flex h-full items-center rounded-md bg-primary-500 px-2 text-xs font-medium text-white transition-all"
                              style={{ width: `${Math.max(pct, 2)}%` }}
                            >
                              {pct > 10 && `${pct}%`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Completed bar */}
                    <div>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="font-medium text-green-700">Completo</span>
                        <span className="text-muted-foreground">{stats.completed} leads</span>
                      </div>
                      <div className="h-6 w-full overflow-hidden rounded-md bg-muted">
                        <div
                          className="flex h-full items-center rounded-md bg-green-500 px-2 text-xs font-medium text-white transition-all"
                          style={{
                            width: `${Math.max(
                              funnelMax > 0 ? Math.round((stats.completed / funnelMax) * 100) : 0,
                              2
                            )}%`,
                          }}
                        >
                          {funnelMax > 0 && Math.round((stats.completed / funnelMax) * 100) > 10 &&
                            `${Math.round((stats.completed / funnelMax) * 100)}%`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Channels */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Canais</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(stats.channels).length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">Sem dados</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(stats.channels)
                      .sort(([, a], [, b]) => b - a)
                      .map(([ch, count]) => (
                        <div key={ch} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            {CHANNEL_LABELS[ch] || ch}
                          </span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                            {count}
                          </span>
                        </div>
                      ))}
                  </div>
                )}

                <div className="mt-4 border-t border-border pt-4">
                  <h4 className="mb-2 text-xs font-medium uppercase text-muted-foreground">
                    Status Geral
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-yellow-700">Em Andamento</span>
                      <span className="font-medium">{stats.inProgress}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-700">Completos</span>
                      <span className="font-medium">{stats.completed}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-red-700">Abandonados</span>
                      <span className="font-medium">{stats.abandoned}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Leads</CardTitle>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Buscar por nome, CPF ou telefone..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-64"
              />
              <Select
                value={channelFilter}
                onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
                options={[
                  { value: "", label: "Todos Canais" },
                  { value: "app", label: "App" },
                  { value: "web", label: "Web" },
                  { value: "whatsapp", label: "WhatsApp" },
                  { value: "link", label: "Link" },
                ]}
                className="w-40"
              />
            </div>
          </div>
        </CardHeader>

        {/* Status filter tabs */}
        <div className="mx-4 flex flex-wrap gap-1 rounded-lg border border-border bg-muted p-1">
          {(["todos", "in_progress", "completed", "abandoned"] as StatusFilter[]).map((tab) => {
            const cfg = tab !== "todos" ? STATUS_CONFIG[tab] : null;
            return (
              <button
                key={tab}
                onClick={() => {
                  setStatusFilter(tab);
                  setPage(1);
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === tab
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab === "todos" ? "Todos" : cfg?.label}
              </button>
            );
          })}
        </div>

        <CardContent className="p-0 pt-3">
          {leadsLoading ? (
            <div className="px-4"><SkeletonTable rows={5} cols={6} /></div>
          ) : leads.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-muted-foreground">
              Nenhum lead encontrado
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Lead
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Canal
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Progresso
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Ultima Atividade
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => {
                    const cfg = STATUS_CONFIG[lead.status] || {
                      label: lead.status,
                      color: "bg-gray-100 text-gray-800",
                    };
                    const progress =
                      lead.totalSteps > 0
                        ? Math.round((lead.currentStep / lead.totalSteps) * 100)
                        : 0;
                    return (
                      <tr
                        key={lead.id}
                        className="border-b border-border hover:bg-muted/30"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">
                            {lead.nome || "Sem nome"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatCpf(lead.cpf)}
                            {lead.whatsapp && ` | ${lead.whatsapp}`}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {CHANNEL_LABELS[lead.channel] || lead.channel}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-16 overflow-hidden rounded-full bg-muted">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  lead.status === "completed"
                                    ? "bg-green-500"
                                    : lead.status === "abandoned"
                                    ? "bg-red-400"
                                    : "bg-yellow-400"
                                }`}
                                style={{ width: `${lead.status === "completed" ? 100 : progress}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {lead.currentStep}/{lead.totalSteps}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={lead.status === "completed" ? "success" : lead.status === "abandoned" ? "error" : "warning"} dot size="md">
                            {cfg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {formatDate(lead.lastActivityAt)}
                        </td>
                        <td className="px-4 py-3">
                          {lead.status !== "completed" && lead.whatsapp && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReengageLead(lead);
                                setReengageMsg("");
                              }}
                            >
                              Re-engajar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-xs text-muted-foreground">
                Mostrando {leads.length} de {total} leads
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 text-xs text-muted-foreground">
                  {page} / {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Proximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Re-engage Modal */}
      {reengageLead && (
        <Dialog
          open={!!reengageLead}
          onOpenChange={() => setReengageLead(null)}
          dirty={reengageMsg.trim() !== ""}
          onSave={async () => { await handleReengage(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Re-engajar Lead via WhatsApp</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p>
                  <strong>Nome:</strong> {reengageLead.nome || "Sem nome"}
                </p>
                <p>
                  <strong>WhatsApp:</strong> {reengageLead.whatsapp}
                </p>
                <p>
                  <strong>Progresso:</strong> Etapa {reengageLead.currentStep} de{" "}
                  {reengageLead.totalSteps}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Mensagem personalizada (opcional)
                </label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={4}
                  value={reengageMsg}
                  onChange={(e) => setReengageMsg(e.target.value)}
                  placeholder={`Ola ${reengageLead.nome || ""}! Notamos que voce iniciou seu cadastro mas nao finalizou. Gostaria de completar?`}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReengageLead(null)}>
                Cancelar
              </Button>
              <Button onClick={handleReengage} disabled={reengaging}>
                {reengaging ? "Enviando..." : "Enviar Mensagem"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
